import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getCurrencySymbol } from '../utils/currency';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

// Quick amount options
const QUICK_AMOUNTS = [5, 10, 20, 50];

export const AddSpendingModal = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const currency = user?.currency || 'GBP';
  const currencySymbol = getCurrencySymbol(currency);
  
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const descriptionInputRef = useRef(null);

  // Handle quick amount selection
  const handleQuickAmount = (quickAmount) => {
    setAmount(quickAmount.toString());
    // Focus on description field for faster input
    setTimeout(() => {
      descriptionInputRef.current?.focus();
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || !description) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/spending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(amount),
          description,
          category: null
        })
      });

      if (response.ok) {
        toast.success('Spending added');
        setAmount('');
        setDescription('');
        onSuccess?.();
      } else {
        throw new Error('Failed to add spending');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto z-50">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add Spending</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4 pb-4">
          <div className="space-y-2">
            <Label className="text-[#9CA3AF]">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">{currencySymbol}</span>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7 h-12 bg-[#252A4A] border-[#2D3354] text-white text-lg placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                data-testid="add-spending-amount-input"
              />
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="flex gap-2 pt-1">
              {QUICK_AMOUNTS.map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => handleQuickAmount(quickAmount)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                    parseFloat(amount) === quickAmount
                      ? 'bg-[#63E6E2] text-[#0F1224]'
                      : 'bg-[#252A4A] text-[#9CA3AF] hover:bg-[#2D3354] hover:text-white active:scale-95'
                  }`}
                  data-testid={`quick-amount-${quickAmount}`}
                >
                  {currencySymbol}{quickAmount}
                </button>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#9CA3AF]">Description</Label>
            <Input
              ref={descriptionInputRef}
              placeholder="e.g., Coffee, Groceries"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-12 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
              data-testid="add-spending-description-input"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-12 border-[#2D3354] text-white hover:bg-white/5"
              data-testid="cancel-spending-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
              data-testid="submit-spending-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[#0F1224]/30 border-t-[#0F1224] rounded-full animate-spin" />
              ) : (
                'Add Spending'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSpendingModal;
